import React, { useState, useRef } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import './ImageUpload.css';

const ImageUpload = ({ onImageUploaded, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Compress image function
  const compressImage = (file, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 800px width/height)
        const maxSize = 800;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      setSuccess(false);
      setUploadProgress(0);
      
      // Compress the image
      const compressedFile = await compressImage(file);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `products/${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, compressedFile);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload error:', error);
          setError(`Upload failed: ${error.message}`);
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploading(false);
            setSuccess(true);
            onImageUploaded(downloadURL);
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(false), 3000);
          } catch (error) {
            console.error('Error getting download URL:', error);
            setError('Failed to get download URL');
            setUploading(false);
          }
        }
      );
    } catch (error) {
      console.error('Error during upload process:', error);
      setError(`Upload failed: ${error.message}`);
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeCurrentImage = () => {
    onImageUploaded('');
    setSuccess(false);
    setError('');
  };

  return (
    <div>
      {/* Upload Area */}
      <div 
        className={`image-upload-container ${dragOver ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="file-input"
        />
        
        {!uploading ? (
          <>
            <div className="upload-icon">📸</div>
            <div className="upload-text">
              {dragOver ? 'Drop your image here!' : 'Upload Product Image'}
            </div>
            <div className="upload-hint">
              Drag & drop an image here, or click to browse
            </div>
            <button type="button" className="upload-button">
              Choose Image
            </button>
          </>
        ) : (
          <>
            <div className="upload-icon">⏳</div>
            <div className="upload-text">Uploading...</div>
            <div className="upload-hint">
              Please wait while we process your image
            </div>
          </>
        )}
      </div>

      {/* Progress Bar */}
      <div className={`progress-container ${uploading ? 'visible' : ''}`}>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
        <div className="progress-text">
          {uploadProgress}% uploaded
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="upload-success">
          <span className="upload-success-icon">✅</span>
          Image uploaded successfully!
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}

      {/* Current Image Preview */}
      {currentImage && (
        <div className="current-image-container">
          <label className="current-image-label">Current Image:</label>
          <div className="current-image-preview">
            <img 
              src={currentImage} 
              alt="Current product" 
              className="current-image"
            />
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeCurrentImage();
              }}
              className="remove-image-btn"
              title="Remove image"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
